import React, { useContext, useEffect } from 'react';
import FormInput from '../../common/FormInput';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
//@ts-ignore
import ToggleButton from '../../common/toggleButton/ToggleButton';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import PanelWithButton from '../../common/panelWithButton/PanelWithButton';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import { PageNames } from '../../../Constants/Common';
import { TyreRepairRequestContext } from '../../../context/TyreRepairRequestContext';
import { TranslationHelper } from 'classes/TranslationHelper';
import { TyreProcessStages } from './TyreRepairRequestTypes';
import { useTranslation } from 'react-i18next';
import { CommonValidation } from 'components/common/commonValidation/commonValidation';
import { useAppSelector } from 'state/hooks';

export type TyreRepairRequestSummaryProps = {
    cancelConfirmText: string;
};

const TyreRepairRequestSummary = ({
    cancelConfirmText
}: TyreRepairRequestSummaryProps): JSX.Element => {
    const {
        vehicle,
        tyreRepairRequest,
        onTogglePermission,
        onFinishedTyreRepairRequest,
        onChangeStage,
        onCancelClick,
        onCancelRepairRequest,
        onCancelUndo,
        onUpdateAddressClick
    } = useContext(TyreRepairRequestContext);

    const {
        showCancelAlert,
        tyreRepairRequests,
        repairAddresses,
        permissionGranted,
        repairAddressSelectedIndex,
        tyreFormErrors: errors
    } = tyreRepairRequest;

    const { t, i18n } = useTranslation();

    const showRepairAddress = useAppSelector((s) => s.site.showRepairAddress);

    const countryCode = i18n.language.toLowerCase();
    const incidentDate = CommonValidation.getDate(
        tyreRepairRequest.dateOfIncident,
        countryCode
    );

    const selectedRepairAddress = repairAddresses[repairAddressSelectedIndex];
    useEffect(() => {
        GoogleAnalytics.setPageView(PageNames.TyreSummary);
    }, []);

    const errorlist = (): Array<string | undefined> => {
        let pageErrors = [errors?.permissionGranted];
        pageErrors = pageErrors.filter((error) => error !== undefined);
        return pageErrors;
    };
    const onUpdateRepairAddress = () => {
        onChangeStage(TyreProcessStages.General);
        onUpdateAddressClick(true);
    };

    const onUpdateWheel = () => {
        onChangeStage(TyreProcessStages.TyreSelection);
    };

    const onBackButtonClick = () => {
        onChangeStage(TyreProcessStages.TyreSelection);
        GoogleAnalytics.trackEventsGoogleAnalytics(
            'Button click',
            'Alloy wheel button clicks',
            'Clicked back button on summary page'
        );
    };

    const forCancelButton = () => {
        const buttonText = t('cancel_repair_request');

        return (
            <button
                aria-label='cancel-button'
                className='btn btn-danger progress-button'
                onClick={onCancelClick}>
                {buttonText}
            </button>
        );
    };
    const cancelButton = forCancelButton();

    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='summary-vehicle-forminput'
                        label={t('vehicle')}>
                        <div
                            id='vehicle-summary-container'
                            className='div-container-style'>
                            <section>
                                <b>{vehicle.vrn}</b>
                                <br />
                                {vehicle.make}
                                <br />
                                {vehicle.model}
                            </section>
                        </div>
                    </FormInput>
                </div>

                {showRepairAddress && (
                    <div className='card-body page-content-body'>
                        <FormInput
                            id='summary-repair-address-forminput'
                            label={t('repair_address')}>
                            <PanelWithButton
                                id='address-summary-container'
                                buttonText={t('button_label.update')}
                                onButtonClick={onUpdateRepairAddress}>
                                <address>
                                    {selectedRepairAddress.addressLine1}
                                    <br />
                                    {selectedRepairAddress.addressLine2}
                                    <br />
                                    {selectedRepairAddress.townOrCity}
                                    <br />
                                    {selectedRepairAddress.region}
                                    <br />
                                    {selectedRepairAddress.postcode}
                                    <br />
                                    {selectedRepairAddress.country}
                                </address>
                            </PanelWithButton>
                        </FormInput>
                    </div>
                )}

                <div className='card-body page-content-body'>
                    <FormInput
                        id='wheels-summary-forminput'
                        label={t('repair_requests')}>
                        {tyreRepairRequests.map((repairRequest, index) => (
                            <PanelWithButton
                                id={`wheel-summary-container-${index.toString()}`}
                                buttonText={t('button_label.update')}
                                onButtonClick={() => onUpdateWheel()}
                                key={index}>
                                <b>{t('claim_incident_date')}</b>
                                {incidentDate}
                                <br />
                                <b>{t('type_of_damage_label')}</b>
                                {TranslationHelper.getTranslatedText(
                                    repairRequest.damageType
                                )}
                                <br />
                                <b>{t('tyre_text')}</b>
                                {TranslationHelper.getTranslatedText(
                                    repairRequest.damagedTyre
                                )}
                            </PanelWithButton>
                        ))}
                    </FormInput>
                </div>
                <hr />
                <div className='card-body page-content-body'>
                    <FormInput
                        id='terms-form'
                        errorText={errors?.permissionGranted}>
                        <div className='col'>
                            <ToggleButton
                                value={permissionGranted}
                                onChange={onTogglePermission}
                                label={t(
                                    'alloy_repair_request.step_3_toggle_permission_label'
                                )}
                            />
                        </div>
                    </FormInput>
                </div>
                <div className='card-footer page-content-footer'>
                    {errors?.permissionGranted && (
                        <ErrorSummary
                            title={t('summary_error')}
                            errors={errorlist()}
                            className='card-error'
                        />
                    )}
                </div>
            </div>
            <div className='bottom-navigation'>
                <button
                    aria-label='submit-button'
                    style={{ width: '100%', marginBottom: '1rem' }}
                    className='btn btn-primary'
                    onClick={onFinishedTyreRepairRequest}>
                    {t('submit')}
                </button>
                <button
                    aria-label='back-button'
                    className='btn btn-secondary progress-button'
                    onClick={() => onBackButtonClick()}>
                    {t('back')}
                </button>
                {cancelButton}
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
        </>
    );
};
export default TyreRepairRequestSummary;
